import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import GoogleAnalytics from '../../analyticsComponents/GoogleAnalytics.js';
import faqsData from './faqs.json';

const FAQItem = ({ question, answer, index }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-gray-200 rounded-xl bg-white shadow-card hover:shadow-card-hover transition-shadow">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between gap-3 text-left px-5 py-4 focus:outline-none focus:ring-2 focus:ring-brand-400 rounded-xl"
        aria-expanded={isOpen}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-brand-50 text-brand-600 font-bold text-xs flex-shrink-0">
            {index + 1}
          </span>
          <span className="font-semibold text-gray-900 text-sm md:text-base">{question}</span>
        </div>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-5 w-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      <div
        className={`overflow-hidden transition-all duration-200 ${
          isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-5 pb-4 pl-[3.75rem] text-gray-500 text-sm leading-relaxed">
          {answer}
        </div>
      </div>
    </div>
  );
};

const FAQPage = ({ isOpen, onClose }) => {
  const [faqs] = useState(faqsData);
  const [analyticsData, setAnalyticsData] = useState(null);

  useEffect(() => {
    if (isOpen) {
      const userId = localStorage.getItem('userId') || 'unknown';
      setAnalyticsData({ userId, eventType: 'view_faq' });
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <Helmet>
        <title>Frequently Asked Questions - Share2Teach</title>
        <meta
          name="description"
          content="Find answers to common questions about Share2Teach, Open Educational Resources, NWU, and NWU Enterprises."
        />
      </Helmet>

      {analyticsData && (
        <GoogleAnalytics
          page="FAQPage"
          userId={analyticsData.userId}
          eventType={analyticsData.eventType}
        />
      )}

      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 overflow-y-auto flex justify-center pt-12 md:pt-16 pb-8"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        {/* Modal */}
        <div className="w-11/12 md:w-4/5 lg:w-2/3 max-w-3xl max-h-[88vh] flex flex-col rounded-2xl shadow-2xl overflow-hidden">

          {/* Header */}
          <div className="relative bg-gray-900 overflow-hidden flex-shrink-0">
            <div className="absolute inset-0 opacity-20">
              <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-brand-500 blur-3xl" />
              <div className="absolute -bottom-16 -left-16 w-48 h-48 rounded-full bg-brand-400 blur-3xl" />
            </div>
            <div className="relative z-10 px-6 md:px-8 py-6 md:py-8">
              <button
                onClick={onClose}
                className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white rounded-full w-8 h-8 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-white/40 transition-colors"
                aria-label="Close FAQ"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div className="flex items-center gap-3 mb-2">
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-brand-600/20 text-brand-300">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div>
                  <h2 className="text-xl md:text-2xl font-bold text-white tracking-tight">Frequently Asked Questions</h2>
                  <p className="text-sm text-gray-400 mt-0.5">Quick answers about Share2Teach and how to get the most out of it.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto bg-gray-50 p-5 md:p-6">
            <div className="space-y-2.5">
              {faqs.map((faq, index) => (
                <FAQItem key={index} question={faq.question} answer={faq.answer} index={index} />
              ))}
            </div>

            {/* Footer hint */}
            <div className="mt-6 text-center">
              <p className="text-xs text-gray-400">
                Still have questions? Reach out to us via the{' '}
                <span className="font-medium text-gray-500">About Us</span> page.
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FAQPage;
